// src/pages/FunctionsPage.js
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { flushSync } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { authService } from '../../services/api';
import Alert from '../../components/common/Alert';
import Modal from '../../components/common/Modal';
import { translateDynamic, useLanguage, getEntityTranslation } from '../../context/LanguageContext';
import { getCategoryIcon } from '../../constants/categoryIcons';
import { categoryService, shiftService } from '../../services/api';
import { renderEntityImage } from '../../utils/imageUtils';
import dedupeByName from '../../utils/dedupeByName';
import tenantStorage from '../../services/tenantStorage';
import { C } from '../../styles/theme';
import { useTheme } from '../../context/ThemeContext';
import host from '../../host';
import useIsMobile from '../../hooks/useIsMobile';

const FONT = "'Plus Jakarta Sans', sans-serif";

const GRADIENT = 'linear-gradient(160deg, var(--c-greenLight) 0%, var(--c-bg) 100%)';

const CATEGORY_BG_MAP = {
    kitchen: '#fffbeb',
    bar: '#eff6ff',
};
const getCategoryBgColor = (category) => {
    const key = category.toLowerCase();
    return CATEGORY_BG_MAP[key] || '#ecfeff';
};

const CATEGORY_ICON_COLOR_MAP = {
    kitchen: '#d97706',
    bar: '#2563eb',
};
const getCategoryIconColor = (category) => {
    const key = category.toLowerCase();
    return CATEGORY_ICON_COLOR_MAP[key] || '#0891b2';
};

/* ── SVG Icons ─────────────────────────────────────────── */
const MonitorIcon = () => (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none"
        stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <rect x="2" y="3" width="20" height="14" rx="2" />
        <path d="M8 21h8" />
        <path d="M12 17v4" />
    </svg>
);

const PhoneIcon = () => (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none"
        stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <rect x="5" y="2" width="14" height="20" rx="2" />
        <circle cx="12" cy="18" r="1" />
    </svg>
);

const ChevronRight = ({ color = 'rgba(255,255,255,0.35)' }) => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
        stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="9 18 15 12 9 6" />
    </svg>
);

const LockIcon = ({ color = 'rgba(255,255,255,0.6)', size = 18 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
        stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
        <path d="M7 11V7a5 5 0 0 1 10 0v4" />
    </svg>
);

const LogOutIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
        stroke={C.mutedLight} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
        <polyline points="16 17 21 12 16 7" />
        <line x1="21" y1="12" x2="9" y2="12" />
    </svg>
);

const ClockIcon = ({ color = 'white' }) => (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none"
        stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10" />
        <polyline points="12 6 12 12 16 14" />
    </svg>
);

const FunctionsPage = () => {
    const { t, language, toggleLanguage, changeLanguage, translationDetails } = useLanguage();
    const navigate = useNavigate();
    const { setAuthenticatedState, tenantConfig, hasPermission, hasFeature, logout } = useAuth();
    const { theme, toggleTheme } = useTheme();

    // Responsive state
    const isMobile = useIsMobile();
    const [activeTab, setActiveTab] = useState('pos');
    const [langDropdownOpen, setLangDropdownOpen] = useState(false);
    const langDropdownRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (langDropdownRef.current && !langDropdownRef.current.contains(e.target)) {
                setLangDropdownOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Business day state
    const [activeSalesDate, setActiveSalesDate] = useState(null);
    const [businessDayActive, setBusinessDayActive] = useState(false);

    useEffect(() => {
        const loadBusinessDay = async () => {
            try {
                const state = await tenantStorage.getBusinessDayState();
                setBusinessDayActive(state.isBusinessDay);
                setActiveSalesDate(state.salesDate);
            } catch (e) {
                console.error('Failed to load business day state:', e);
            }
        };
        loadBusinessDay();
    }, []);

    // KDS categories from API
    const [apiKdsCategories, setApiKdsCategories] = useState(null);

    useEffect(() => {
        let mounted = true;
        categoryService.getAll().then(cats => {
            if (!mounted) return;
            const kdsCats = dedupeByName(cats).filter(c => c.is_kitchen_display);
            setApiKdsCategories(kdsCats.sort((a, b) => a.name.localeCompare(b.name)));
        }).catch(err => {
            console.error('Failed to load categories for KDS:', err);
        });
        return () => { mounted = false; };
    }, []);

    // Printer check: no shipping host has a silent-print bridge to probe for
    // a default OS printer, so this warning is never set — kept as dead
    // state only because the Alert below still renders it if it ever is.
    const [printerWarning, setPrinterWarning] = useState('');

    // UI state
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [showEmployeeModal, setShowEmployeeModal] = useState(false);
    const [selectedFunction, setSelectedFunction] = useState('');
    const [employeePin, setEmployeePin] = useState('');

    // Clock In/Out modal state
    const [clockModal, setClockModal] = useState(false);
    const [clockMode, setClockMode] = useState('choose'); // 'choose' | 'pin' | 'result' | 'error'
    const [clockAction, setClockAction] = useState(null); // 'in' | 'out'
    const [pin, setPin] = useState('');
    const [clockResult, setClockResult] = useState(null);
    const [clockError, setClockError] = useState('');
    const [clockLoading, setClockLoading] = useState(false);

    const resetClockState = () => {
        setClockMode('choose');
        setClockAction(null);
        setPin('');
        setClockResult(null);
        setClockError('');
        setClockLoading(false);
    };

    const handleClockSubmit = async () => {
        if (!pin || pin.length < 4) return;
        setClockLoading(true);
        try {
            const result = clockAction === 'in'
                ? await shiftService.clockIn(pin)
                : await shiftService.clockOut(pin);
            setClockResult(result);
            if (clockAction === 'in' && result.station === 'Unassigned') {
                setClockMode('pick_station');
            } else {
                setClockMode('result');
                setTimeout(() => { setClockModal(false); resetClockState(); }, 4000);
            }
        } catch (err) {
            setClockError(err.response?.data?.detail || t('clock_failed') + ' ' + clockAction);
            setClockMode('error');
        } finally {
            setClockLoading(false);
        }
    };

    const handleStationPick = async (station) => {
        if (!clockResult?.shift_id) return;
        try {
            await shiftService.patchStation(clockResult.shift_id, station);
            setClockResult({ ...clockResult, station });
            setClockMode('result');
            setTimeout(() => { setClockModal(false); resetClockState(); }, 4000);
        } catch {
            setClockMode('result');
            setTimeout(() => { setClockModal(false); resetClockState(); }, 4000);
        }
    };

    const functionCards = useMemo(() => {
        const cards = [
            {
                id: 'sales',
                title: t('Cashier Sales'),
                subtitle: t('Employee PIN required'),
                icon: <MonitorIcon />,
                requiresEmployee: true,
                allowedRoles: ['admin', 'manager'],
                path: '/functions/cashier-sales',
            },
            {
                id: 'table_service',
                title: t('table_service'),
                subtitle: t('Employee PIN required'),
                icon: <PhoneIcon />,
                requiresEmployee: true,
                allowedRoles: ['admin', 'manager'],
                path: '/functions/table-service',
            },
            {
                id: 'clock_in_out',
                title: t('clock_in_out'),
                subtitle: t('enter_pin'),
                icon: <ClockIcon color={C.green} />,
                requiresEmployee: false,
                isClockAction: true,
            },
        ];
        // Unreachable: FunctionsPage is browser-only, and host.isPosDevice is only ever true on Capacitor.
        if (host.isPosDevice) {
            return cards.filter(c => {
                if (c.id === 'table_service') return false; // unreachable: host.isPosDevice is never true here
                if (c.id === 'clock_in_out') return hasFeature('clock_in');
                return true;
            });
        }
        // On web: gate table_service and clock_in_out by feature flags
        return cards.filter(c => {
            if (c.id === 'table_service') return hasFeature('table_service');
            if (c.id === 'clock_in_out') return hasFeature('clock_in');
            return true;
        });
    }, [t, language, hasFeature]);

    const kdsCategories = useMemo(() => {
        if (!hasPermission('pos:kds') || !hasFeature('kds')) return [];
        // Use API-loaded categories if available, fallback to tenantConfig
        if (apiKdsCategories && apiKdsCategories.length > 0) {
            return apiKdsCategories.map(cat => ({
                id: `orders_${cat.name.toLowerCase().replace(/\s+/g, '_')}`,
                title: getEntityTranslation(cat, language, translationDetails) || translateDynamic(cat.name, t),
                subtitle: t('Live order display'),
                category: cat.name,
                iconKey: cat.icon || 'star',
                imageUrl: cat.image_url || null,
                path: `/functions/orders/${encodeURIComponent(cat.name)}`,
            }));
        }
        // Fallback: categories API failed, return empty (config keys removed)
        return [];
    }, [tenantConfig, t, language, hasPermission, hasFeature, apiKdsCategories, translationDetails]);

    // Lock every function except Cashier Sales (which is where the business day is started)
    // until there is an active business day.
    const isBusinessDayLocked = (id) => !businessDayActive && id !== 'sales';

    // Handle function card click
    const handleFunctionClick = (func) => {
        if (isBusinessDayLocked(func.id)) {
            setError(t('start_business_day_to_access') || 'Start a business day in Cashier Sales to access this');
            setTimeout(() => setError(''), 4000);
            return;
        }
        if (func.isClockAction) {
            resetClockState();
            setClockModal(true);
            return;
        }
        setSelectedFunction(func);
        if (func.requiresEmployee) {
            setShowEmployeeModal(true);
            setEmployeePin('');
        } else {
            handleDirectAccess(func);
        }
    };

    const handleKdsClick = (kds) => {
        if (isBusinessDayLocked(kds.id)) {
            setError(t('start_business_day_to_access') || 'Start a business day in Cashier Sales to access this');
            setTimeout(() => setError(''), 4000);
            return;
        }
        navigate(kds.path);
    };

    // Handle employee authentication
    const handleEmployeeAuth = async (e) => {
        e.preventDefault();
        if (!employeePin || employeePin.length !== 6) {
            setError(t('Please enter your 6-digit PIN'));
            return;
        }
        try {
            setError('');
            setLoading(true);
            const tenantId = localStorage.getItem('tenant_id');
            const employeeAuthData = await authService.employeeLogin(tenantId, employeePin);

            const isSalesFunction = selectedFunction?.id === 'sales' || selectedFunction?.id === 'table_service';
            const hasRole = !selectedFunction?.allowedRoles || selectedFunction.allowedRoles.includes(employeeAuthData.user_role);
            const allowedAsCashier = isSalesFunction && employeeAuthData.is_cashier;

            if (!hasRole && !allowedAsCashier) {
                setError(t('Access denied. Cashier access is required for this function.'));
                setLoading(false);
                return;
            }

            // Apply employee's preferred language for sales pages
            if (employeeAuthData.preferred_language) {
                changeLanguage(employeeAuthData.preferred_language);
            }

            flushSync(() => {
                setAuthenticatedState({
                    isAuthenticated: true,
                    userRole: employeeAuthData.user_role,
                    username: employeeAuthData.employee_name,
                    tenantId: tenantId,
                    employeeId: employeeAuthData.employee_id,
                    authType: 'function',
                });
                setShowEmployeeModal(false);
                setLoading(false);
            });
            navigate(selectedFunction.path);
        } catch (err) {
            console.error('Employee authentication error:', err);
            setError(t('Invalid employee credentials'));
            setLoading(false);
        }
    };

    const handleDirectAccess = (func) => {
        navigate(func.path);
    };

    const handleSignOut = () => {
        if (host.isPosDevice) {
            // Device reset: confirm before wiping all local data
            if (window.confirm(t('device_reset_confirm') || 'This will erase all local data and require a fresh login. Continue?')) {
                logout();
            }
        } else {
            logout();
        }
    };

    const brandName = tenantConfig?.brand_name || 'Aqana';

    const renderBusinessDayBanner = () => {
        if (businessDayActive && activeSalesDate) {
            let dateStr = activeSalesDate;
            try {
                const parts = (activeSalesDate || '').split(' ');
                const d = new Date(parts[0] + 'T00:00:00');
                const datePart = d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
                const timePart = parts[1] ? parts[1].substring(0, 5) : '';
                dateStr = timePart ? `${datePart} · ${timePart}` : datePart;
            } catch {}
            return (
                <div style={{
                    background: '#eff5ff',
                    border: '1px solid #bfdbfe',
                    borderRadius: 8,
                    padding: '10px 16px',
                    marginBottom: 16,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    fontFamily: FONT,
                }}>
                    <div style={{
                        width: 8, height: 8, borderRadius: '50%',
                        background: '#2563eb',
                    }} />
                    <span style={{ fontSize: 13, fontWeight: 600, color: '#2563eb' }}>
                        {t('business_day_active') || 'Business Day Active'}
                    </span>
                    <span style={{ fontSize: 13, color: '#475569' }}>
                        {dateStr}
                    </span>
                </div>
            );
        }
        return (
            <div style={{
                background: '#fef2f2',
                border: '1px solid #fecaca',
                borderRadius: 8,
                padding: '10px 16px',
                marginBottom: 16,
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                fontFamily: FONT,
            }}>
                <div style={{
                    width: 8, height: 8, borderRadius: '50%',
                    background: '#dc2626',
                }} />
                <span style={{ fontSize: 13, fontWeight: 600, color: '#dc2626' }}>
                    {t('no_active_business_day') || 'No Active Business Day'}
                </span>
            </div>
        );
    };

    /* ── Shared sub-components ─────────────────────────── */

    // Desktop glass POS card
    const GlassPosCard = ({ func }) => {
        const bdLock = isBusinessDayLocked(func.id);
        const disabled = bdLock;
        const lockMsg = t('start_business_day_to_access') || 'Start a business day in Cashier Sales to access this';
        return (
            <div
                onClick={() => handleFunctionClick(func)}
                title={bdLock ? lockMsg : undefined}
                style={{
                    background: C.white,
                    backdropFilter: 'blur(10px)',
                    WebkitBackdropFilter: 'blur(10px)',
                    border: `1px solid ${C.border}`,
                    borderRadius: 14,
                    padding: '16px 18px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 14,
                    cursor: disabled ? 'not-allowed' : 'pointer',
                    opacity: disabled ? 0.5 : 1,
                    transition: 'background 0.2s, transform 0.15s',
                    fontFamily: FONT,
                }}
                onMouseEnter={(e) => { if (!disabled) e.currentTarget.style.background = C.faint; }}
                onMouseLeave={(e) => { if (!disabled) e.currentTarget.style.background = C.white; }}
            >
                <div style={{
                    width: 48, height: 48,
                    background: C.greenLight, color: C.green,
                    borderRadius: 12,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    flexShrink: 0,
                }}>
                    {func.icon}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 16, fontWeight: 700, color: C.text }}>{func.title}</div>
                    <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>
                        {bdLock ? lockMsg : func.subtitle}
                    </div>
                </div>
                {bdLock ? <LockIcon color={C.mutedLight} /> : <ChevronRight />}
            </div>
        );
    };

    // Desktop right-panel KDS card
    const KdsCard = ({ kds }) => {
        const bdLock = isBusinessDayLocked(kds.id);
        const disabled = bdLock;
        const lockMsg = t('start_business_day_to_access') || 'Start a business day in Cashier Sales to access this';
        const bgColor = getCategoryBgColor(kds.category);
        const iconColor = getCategoryIconColor(kds.category);
        return (
            <div
                onClick={() => handleKdsClick(kds)}
                title={bdLock ? lockMsg : undefined}
                style={{
                    background: C.white,
                    border: `1.5px solid ${C.border}`,
                    borderRadius: 12,
                    padding: '16px 14px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    cursor: disabled ? 'not-allowed' : 'pointer',
                    opacity: disabled ? 0.5 : 1,
                    transition: 'border-color 0.2s, box-shadow 0.2s',
                    fontFamily: FONT,
                    minWidth: 180,
                    flexShrink: 0,
                }}
                onMouseEnter={(e) => {
                    if (!disabled) {
                        e.currentTarget.style.borderColor = C.greenBorder;
                        e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.06)';
                    }
                }}
                onMouseLeave={(e) => {
                    if (!disabled) {
                        e.currentTarget.style.borderColor = C.border;
                        e.currentTarget.style.boxShadow = 'none';
                    }
                }}
            >
                <div style={{
                    width: 36, height: 36,
                    background: bgColor,
                    borderRadius: 10,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    flexShrink: 0,
                }}>
                    {kds.imageUrl ? renderEntityImage(kds.imageUrl, 22) : getCategoryIcon(kds.iconKey, { size: 18, color: iconColor })}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: C.text }}>{kds.title}</div>
                    <div style={{ fontSize: 10, color: C.mutedLight, marginTop: 1 }}>{kds.subtitle}</div>
                </div>
                {bdLock ? <LockIcon color={C.mutedLight} /> : <ChevronRight color={C.border} />}
            </div>
        );
    };

    // Mobile POS card (white card with colored icon container)
    const MobilePosCard = ({ func }) => {
        const bdLock = isBusinessDayLocked(func.id);
        const disabled = bdLock;
        const lockMsg = t('start_business_day_to_access') || 'Start a business day in Cashier Sales to access this';
        return (
            <div
                onClick={() => handleFunctionClick(func)}
                title={bdLock ? lockMsg : undefined}
                style={{
                    background: C.white,
                    border: `1.5px solid ${C.border}`,
                    borderRadius: 14,
                    padding: '16px 16px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 14,
                    cursor: disabled ? 'not-allowed' : 'pointer',
                    opacity: disabled ? 0.5 : 1,
                    fontFamily: FONT,
                }}
            >
                <div style={{
                    width: 48, height: 48,
                    background: C.green,
                    borderRadius: 12,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    flexShrink: 0,
                }}>
                    {func.icon}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 15, fontWeight: 700, color: C.text }}>{func.title}</div>
                    <div style={{ fontSize: 11, color: C.mutedLight, marginTop: 2 }}>
                        {bdLock ? lockMsg : func.subtitle}
                    </div>
                </div>
                {bdLock ? <LockIcon color={C.mutedLight} /> : <ChevronRight color={C.mutedLight} />}
            </div>
        );
    };

    // Theme toggle — icon button (rendered inside the top-right control cluster)
    const ThemeToggle = () => (
        <button
            onClick={toggleTheme}
            title={theme === 'dark' ? t('light_mode') : t('dark_mode')}
            aria-label="Toggle theme"
            style={{
                background: 'none', border: 'none', cursor: 'pointer',
                padding: 6, borderRadius: 6, display: 'flex', alignItems: 'center',
                color: C.muted,
            }}
        >
            {theme === 'dark'
                ? <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="5"/><line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/><line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/><line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/><line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/></svg>
                : <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/></svg>}
        </button>
    );

    // Language toggle
    const LangToggle = () => (
        <div ref={langDropdownRef} style={{ position: 'relative' }}>
            <button
                onClick={() => setLangDropdownOpen(!langDropdownOpen)}
                style={{
                    background: 'none', border: 'none', cursor: 'pointer',
                    padding: 6, borderRadius: 6, display: 'flex',
                    alignItems: 'center', color: '#64748b',
                }}
                title="Language"
            >
                <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10"/>
                    <path d="M2 12h20M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
                </svg>
            </button>
            {langDropdownOpen && (
                <div style={{
                    position: 'absolute', top: '100%', right: 0, marginTop: 4,
                    background: '#fff', border: '1px solid #e2e8f0',
                    borderRadius: 8, boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                    minWidth: 140, zIndex: 1600, overflow: 'hidden',
                }}>
                    <button
                        onClick={() => { if (language !== 'en') toggleLanguage(); setLangDropdownOpen(false); }}
                        style={{
                            width: '100%', padding: '8px 14px', border: 'none', cursor: 'pointer',
                            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                            fontSize: 13, fontWeight: 500, fontFamily: FONT,
                            background: language === 'en' ? '#eff5ff' : '#fff',
                            color: language === 'en' ? '#2563eb' : '#374151',
                        }}
                    >
                        English {language === 'en' && <span style={{ color: '#2563eb' }}>&#10003;</span>}
                    </button>
                    <button
                        onClick={() => { if (language !== 'ar') toggleLanguage(); setLangDropdownOpen(false); }}
                        style={{
                            width: '100%', padding: '8px 14px', border: 'none', cursor: 'pointer',
                            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                            fontSize: 13, fontWeight: 500, fontFamily: FONT,
                            background: language === 'ar' ? '#eff5ff' : '#fff',
                            color: language === 'ar' ? '#2563eb' : '#374151',
                        }}
                    >
                        العربية {language === 'ar' && <span style={{ color: '#2563eb' }}>&#10003;</span>}
                    </button>
                </div>
            )}
        </div>
    );

    // Sign out / device reset link
    const SignOutLink = ({ style = {} }) => (
        <div
            onClick={handleSignOut}
            style={{
                display: 'flex', alignItems: 'center', gap: 6,
                cursor: 'pointer', fontFamily: FONT,
                fontSize: 12, fontWeight: 600,
                color: host.isPosDevice ? C.red : C.mutedLight,
                ...style,
            }}
        >
            <LogOutIcon />
            <span>{host.isPosDevice ? (t('reset_device') || 'Reset Device') : t('Sign Out')}</span>
        </div>
    );

    // Top-right control cluster — theme toggle, language, sign out (single row)
    const TopRightControls = () => (
        <div style={{
            position: 'fixed', top: 16, right: 16, zIndex: 1500,
            display: 'flex', alignItems: 'center', gap: 10,
        }}>
            <ThemeToggle />
            <LangToggle />
            <SignOutLink />
        </div>
    );

    /* ── Clock In/Out Modal ────────────────────────────── */
    const renderClockModal = () => {
        if (!clockModal) return null;
        return (
            <div
                style={{
                    position: 'fixed', inset: 0, zIndex: 2000,
                    background: 'rgba(0,0,0,0.5)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    padding: 20,
                    fontFamily: FONT,
                }}
                onClick={(e) => { if (e.target === e.currentTarget) { setClockModal(false); resetClockState(); } }}
            >
                <div style={{
                    background: C.white,
                    borderRadius: 16,
                    width: '100%',
                    maxWidth: 420,
                    boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
                    overflow: 'hidden',
                }}>
                    {/* Header */}
                    <div style={{
                        padding: '20px 24px 16px',
                        borderBottom: `1px solid ${C.border}`,
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                            <div style={{
                                width: 36, height: 36, borderRadius: 10,
                                background: C.greenLight,
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                            }}>
                                <ClockIcon color={C.green} />
                            </div>
                            <span style={{ fontSize: 16, fontWeight: 800, color: C.text }}>
                                {t('clock_in_out')}
                            </span>
                        </div>
                        <button
                            onClick={() => { setClockModal(false); resetClockState(); }}
                            style={{
                                background: 'none', border: 'none', cursor: 'pointer',
                                fontSize: 20, color: C.muted, lineHeight: 1, padding: 4,
                            }}
                            aria-label="Close"
                        >
                            &times;
                        </button>
                    </div>

                    {/* Body */}
                    <div style={{ padding: '24px 24px 28px' }}>
                        {/* Screen 1: Choose action */}
                        {clockMode === 'choose' && (
                            <div>
                                <p style={{ fontSize: 13, color: C.muted, marginBottom: 20, margin: '0 0 20px' }}>
                                    {t('clock_choose_action') || 'Choose an action to continue.'}
                                </p>
                                <div style={{ display: 'flex', gap: 12 }}>
                                    <button
                                        onClick={() => { setClockAction('in'); setClockMode('pin'); }}
                                        style={{
                                            flex: 1, padding: '18px 0',
                                            borderRadius: 12, border: 'none',
                                            background: C.green, color: C.white,
                                            fontSize: 15, fontWeight: 700,
                                            cursor: 'pointer', fontFamily: FONT,
                                            display: 'flex', flexDirection: 'column',
                                            alignItems: 'center', gap: 8,
                                            transition: 'background 0.15s',
                                        }}
                                        onMouseEnter={e => e.currentTarget.style.background = C.greenDark}
                                        onMouseLeave={e => e.currentTarget.style.background = C.green}
                                    >
                                        <ClockIcon color={C.white} />
                                        {t('clock_in')}
                                    </button>
                                    <button
                                        onClick={() => { setClockAction('out'); setClockMode('pin'); }}
                                        style={{
                                            flex: 1, padding: '18px 0',
                                            borderRadius: 12,
                                            border: `2px solid ${C.border}`,
                                            background: C.white, color: C.text,
                                            fontSize: 15, fontWeight: 700,
                                            cursor: 'pointer', fontFamily: FONT,
                                            display: 'flex', flexDirection: 'column',
                                            alignItems: 'center', gap: 8,
                                            transition: 'border-color 0.15s',
                                        }}
                                        onMouseEnter={e => e.currentTarget.style.borderColor = C.muted}
                                        onMouseLeave={e => e.currentTarget.style.borderColor = C.border}
                                    >
                                        <ClockIcon color={C.muted} />
                                        {t('clock_out')}
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* Screen 2: PIN entry */}
                        {clockMode === 'pin' && (
                            <div>
                                <p style={{ fontSize: 13, color: C.muted, marginBottom: 6, margin: '0 0 6px' }}>
                                    {clockAction === 'in' ? t('clock_in') : t('clock_out')}
                                </p>
                                <p style={{ fontSize: 13, fontWeight: 600, color: C.text, marginBottom: 20, margin: '0 0 20px' }}>
                                    {t('enter_pin')}
                                </p>
                                <input
                                    type="password"
                                    inputMode="numeric"
                                    maxLength={6}
                                    placeholder="••••••"
                                    value={pin}
                                    onChange={e => setPin(e.target.value.replace(/\D/g, '').slice(0, 6))}
                                    autoFocus
                                    onKeyDown={e => { if (e.key === 'Enter') handleClockSubmit(); }}
                                    style={{
                                        width: '100%', boxSizing: 'border-box',
                                        padding: '14px 16px',
                                        borderRadius: 10, border: `1.5px solid ${C.border}`,
                                        fontSize: 22, textAlign: 'center',
                                        fontFamily: FONT, color: C.text,
                                        letterSpacing: 6, outline: 'none',
                                        marginBottom: 16,
                                    }}
                                />
                                <div style={{ display: 'flex', gap: 10 }}>
                                    <button
                                        onClick={() => { setClockMode('choose'); setPin(''); }}
                                        style={{
                                            flex: 1, padding: '12px 0',
                                            borderRadius: 10, border: `1.5px solid ${C.border}`,
                                            background: C.white, color: C.muted,
                                            fontSize: 14, fontWeight: 600,
                                            cursor: 'pointer', fontFamily: FONT,
                                        }}
                                    >
                                        {t('Cancel')}
                                    </button>
                                    <button
                                        onClick={handleClockSubmit}
                                        disabled={clockLoading || pin.length < 4}
                                        style={{
                                            flex: 2, padding: '12px 0',
                                            borderRadius: 10, border: 'none',
                                            background: pin.length >= 4 ? C.green : C.border,
                                            color: pin.length >= 4 ? C.white : C.muted,
                                            fontSize: 14, fontWeight: 700,
                                            cursor: pin.length >= 4 ? 'pointer' : 'not-allowed',
                                            fontFamily: FONT,
                                            transition: 'background 0.15s',
                                        }}
                                    >
                                        {clockLoading ? '...' : t('submit') || 'Submit'}
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* Screen 3a: Pick station for ad-hoc shift */}
                        {clockMode === 'pick_station' && clockResult && (() => {
                            const stationColors = { Kitchen: '#d97706', Bar: '#2563eb', Cashier: '#2563eb', FOH: '#7c3aed' };
                            const stationBgs = { Kitchen: '#fffbeb', Bar: '#eff6ff', Cashier: '#eff5ff', FOH: '#f5f3ff' };
                            const pref = clockResult.preferred_station;
                            const others = ['Kitchen', 'Bar', 'Cashier', 'FOH'].filter(s => s !== pref);
                            return (
                                <div style={{ textAlign: 'center' }}>
                                    <div style={{ fontSize: 16, fontWeight: 700, color: C.text, marginBottom: 4 }}>
                                        {clockResult.employee_name}
                                    </div>
                                    <div style={{ fontSize: 13, color: C.muted, marginBottom: 16 }}>
                                        {t('select_station') || 'Select your station'}
                                    </div>

                                    {pref && (
                                        <button
                                            onClick={() => handleStationPick(pref)}
                                            style={{
                                                width: '100%', padding: '16px 12px', borderRadius: 10, cursor: 'pointer',
                                                border: `2px solid ${stationColors[pref] || C.green}`,
                                                background: stationBgs[pref] || C.greenLight,
                                                color: stationColors[pref] || C.green,
                                                fontSize: 15, fontWeight: 700, fontFamily: FONT,
                                                marginBottom: 6,
                                            }}
                                        >
                                            {t(pref) || pref}
                                            <div style={{ fontSize: 10, fontWeight: 500, marginTop: 2, opacity: 0.7 }}>
                                                {t('preferred_station') || 'Preferred station'}
                                            </div>
                                        </button>
                                    )}

                                    <div style={{ display: 'grid', gridTemplateColumns: pref ? '1fr 1fr 1fr' : '1fr 1fr', gap: 8, marginBottom: 12, marginTop: pref ? 6 : 0 }}>
                                        {(pref ? others : ['Kitchen', 'Bar', 'Cashier', 'FOH']).map(station => (
                                            <button
                                                key={station}
                                                onClick={() => handleStationPick(station)}
                                                style={{
                                                    padding: pref ? '10px 8px' : '14px 12px', borderRadius: 10, cursor: 'pointer',
                                                    border: `1.5px solid ${stationColors[station]}`,
                                                    background: stationBgs[station], color: stationColors[station],
                                                    fontSize: pref ? 12 : 14, fontWeight: 600, fontFamily: FONT,
                                                }}
                                            >
                                                {t(station) || station}
                                            </button>
                                        ))}
                                    </div>
                                    <button
                                        onClick={() => { setClockMode('result'); setTimeout(() => { setClockModal(false); resetClockState(); }, 4000); }}
                                        style={{ fontSize: 12, color: C.mutedLight, background: 'none', border: 'none', cursor: 'pointer', fontFamily: FONT }}
                                    >
                                        {t('skip') || 'Skip'}
                                    </button>
                                </div>
                            );
                        })()}

                        {/* Screen 3: Success result */}
                        {clockMode === 'result' && clockResult && (
                            <div style={{ textAlign: 'center' }}>
                                <div style={{
                                    width: 60, height: 60, borderRadius: '50%',
                                    background: C.greenLight,
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    margin: '0 auto 16px',
                                }}>
                                    <svg width="28" height="28" viewBox="0 0 24 24" fill="none"
                                        stroke={C.green} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                        <polyline points="20 6 9 17 4 12" />
                                    </svg>
                                </div>
                                <div style={{ fontSize: 18, fontWeight: 800, color: C.text, marginBottom: 6 }}>
                                    {clockResult.employee_name || ''}
                                </div>
                                {clockResult.station && (
                                    <div style={{ fontSize: 13, color: C.muted, marginBottom: 4 }}>
                                        {clockResult.station}
                                    </div>
                                )}
                                {(clockResult.scheduled_start || clockResult.scheduled_end) && (
                                    <div style={{ fontSize: 12, color: C.mutedLight, marginBottom: 8 }}>
                                        {clockResult.scheduled_start} – {clockResult.scheduled_end}
                                    </div>
                                )}
                                <div style={{
                                    display: 'inline-block',
                                    background: C.greenLight,
                                    borderRadius: 8,
                                    padding: '6px 14px',
                                    fontSize: 13, fontWeight: 700, color: C.green,
                                    marginTop: 4,
                                }}>
                                    {clockAction === 'in' ? t('clocked_in_at') : t('clocked_out_at')}{' '}
                                    {clockResult.clock_in_time || clockResult.clock_out_time || ''}
                                </div>
                                <div style={{ fontSize: 11, color: C.mutedLight, marginTop: 12 }}>
                                    {t('auto_dismiss') || 'Closing automatically...'}
                                </div>
                            </div>
                        )}

                        {/* Screen 4: Error */}
                        {clockMode === 'error' && (
                            <div style={{ textAlign: 'center' }}>
                                <div style={{
                                    width: 60, height: 60, borderRadius: '50%',
                                    background: '#fef2f2',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    margin: '0 auto 16px',
                                }}>
                                    <svg width="28" height="28" viewBox="0 0 24 24" fill="none"
                                        stroke={C.red} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                        <circle cx="12" cy="12" r="10" />
                                        <line x1="15" y1="9" x2="9" y2="15" />
                                        <line x1="9" y1="9" x2="15" y2="15" />
                                    </svg>
                                </div>
                                <div style={{ fontSize: 15, fontWeight: 700, color: C.text, marginBottom: 8 }}>
                                    {clockError}
                                </div>
                                <button
                                    onClick={() => { setClockMode('pin'); setPin(''); setClockError(''); }}
                                    style={{
                                        padding: '10px 28px',
                                        borderRadius: 10, border: 'none',
                                        background: C.green, color: C.white,
                                        fontSize: 14, fontWeight: 700,
                                        cursor: 'pointer', fontFamily: FONT,
                                        marginTop: 4,
                                    }}
                                >
                                    {t('try_again') || 'Try Again'}
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    };

    /* ── Desktop Layout ────────────────────────────────── */
    if (!isMobile) {
        return (
            <div style={{ display: 'flex', flexDirection: 'column', height: '100vh', fontFamily: FONT }}>
                <TopRightControls />
                {error && (
                    <div style={{ position: 'fixed', top: 16, left: '50%', transform: 'translateX(-50%)', zIndex: 1000, width: '90%', maxWidth: 400 }}>
                        <Alert type="error" message={error} onClose={() => setError('')} />
                    </div>
                )}
                {printerWarning && (
                    <div style={{ position: 'fixed', top: 16, left: '50%', transform: 'translateX(-50%)', zIndex: 999, width: '90%', maxWidth: 500 }}>
                        <Alert type="warning" message={printerWarning} onClose={() => setPrinterWarning('')} />
                    </div>
                )}

                {/* Main panel — green gradient, full width */}
                <div style={{
                    flex: 1,
                    background: GRADIENT,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: 40,
                }}>
                    <div style={{ width: '100%', maxWidth: 360 }}>
                        <div style={{
                            fontSize: 12, fontWeight: 700, letterSpacing: 2,
                            color: C.muted, textTransform: 'uppercase',
                            marginBottom: 8,
                        }}>
                            {brandName}
                        </div>
                        <div style={{
                            fontSize: 26, fontWeight: 800, color: C.text,
                            marginBottom: 36,
                        }}>
                            {t('Select Your Station')}
                        </div>
                        {renderBusinessDayBanner()}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                            {functionCards.map(func => (
                                <GlassPosCard key={func.id} func={func} />
                            ))}
                        </div>
                    </div>
                </div>

                {/* Bottom section — KDS displays + utilities */}
                <div style={{
                    borderTop: `1px solid ${C.border}`,
                    background: C.faint,
                    padding: kdsCategories.length > 0 ? '20px 28px 24px' : '14px 28px',
                    position: 'relative',
                }}>
                    {kdsCategories.length > 0 ? (
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                            <div style={{
                                fontSize: 10, fontWeight: 700, textTransform: 'uppercase',
                                letterSpacing: 1.5, color: C.mutedLight,
                                marginBottom: 14,
                            }}>
                                {t('Display')}
                            </div>
                            <div style={{
                                display: 'flex', flexDirection: 'row', gap: 12,
                                overflowX: 'auto', justifyContent: 'center',
                                maxWidth: '100%', paddingBottom: 2,
                            }}>
                                {kdsCategories.map(kds => (
                                    <KdsCard key={kds.id} kds={kds} />
                                ))}
                            </div>
                        </div>
                    ) : (
                        <div style={{ height: 20 }} />
                    )}

                </div>

                {/* Employee Auth Modal */}
                <Modal
                    isOpen={showEmployeeModal}
                    onClose={() => setShowEmployeeModal(false)}
                    title={t('Employee Login - {functionTitle}', { functionTitle: selectedFunction?.title })}
                >
                    <form onSubmit={handleEmployeeAuth} className="space-y-4">
                        <div style={{ marginBottom: 16 }}>
                            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: C.text, marginBottom: 6 }}>
                                {t('Enter your 6-digit PIN')} <span style={{ color: C.red }}>*</span>
                            </label>
                            <input
                                type="password"
                                maxLength={6}
                                pattern="[0-9]{6}"
                                inputMode="numeric"
                                placeholder="••••••"
                                value={employeePin}
                                onChange={e => setEmployeePin(e.target.value.replace(/\D/g, '').slice(0, 6))}
                                autoFocus
                                required
                                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                            />
                        </div>
                        <div className="flex justify-end space-x-3 pt-4">
                            <button
                                type="button"
                                onClick={() => setShowEmployeeModal(false)}
                                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                            >
                                {t('Cancel')}
                            </button>
                            <button
                                type="submit"
                                disabled={loading}
                                className="px-4 py-2 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-md shadow-sm hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:bg-gray-400"
                            >
                                {loading ? t('Authenticating...') : t('Login')}
                            </button>
                        </div>
                    </form>
                </Modal>
                {renderClockModal()}
            </div>
        );
    }

    /* ── Mobile Layout ─────────────────────────────────── */
    return (
        <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', fontFamily: FONT }}>
            <TopRightControls />
            {error && (
                <div style={{ position: 'fixed', top: 16, left: '50%', transform: 'translateX(-50%)', zIndex: 1000, width: '90%', maxWidth: 400 }}>
                    <Alert type="error" message={error} onClose={() => setError('')} />
                </div>
            )}
            {printerWarning && (
                <div style={{ position: 'fixed', top: 16, left: '50%', transform: 'translateX(-50%)', zIndex: 999, width: '90%', maxWidth: 500 }}>
                    <Alert type="warning" message={printerWarning} onClose={() => setPrinterWarning('')} />
                </div>
            )}

            {/* Header with gradient */}
            <div style={{
                background: GRADIENT,
                padding: '48px 20px 0',
                textAlign: 'center',
            }}>
                <div style={{
                    fontSize: 12, fontWeight: 700, letterSpacing: 2,
                    color: C.muted, textTransform: 'uppercase',
                    marginBottom: 8,
                }}>
                    {brandName}
                </div>
                <div style={{
                    fontSize: 22, fontWeight: 800, color: C.text,
                    marginBottom: 28,
                }}>
                    {t('Select Your Station')}
                </div>

                {/* Tab bar */}
                <div style={{ display: 'flex', gap: 0 }}>
                    {[
                        { key: 'pos', label: t('Point of Sale') },
                        { key: 'display', label: t('Display') },
                    ].map(tab => {
                        const active = activeTab === tab.key;
                        return (
                            <div
                                key={tab.key}
                                onClick={() => setActiveTab(tab.key)}
                                style={{
                                    flex: 1,
                                    padding: '12px 0',
                                    fontSize: 13,
                                    fontWeight: active ? 700 : 600,
                                    color: active ? C.green : C.muted,
                                    cursor: 'pointer',
                                    borderBottom: active ? `2px solid ${C.green}` : `2px solid ${C.border}`,
                                    background: active ? C.greenLight : 'transparent',
                                    borderRadius: active ? '8px 8px 0 0' : 0,
                                    transition: 'all 0.2s',
                                    fontFamily: FONT,
                                }}
                            >
                                {tab.label}
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* Body */}
            <div style={{
                flex: 1,
                background: C.white,
                padding: '20px 16px',
                display: 'flex',
                flexDirection: 'column',
            }}>
                {renderBusinessDayBanner()}
                {activeTab === 'pos' && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                        {functionCards.map(func => (
                            <MobilePosCard key={func.id} func={func} />
                        ))}
                    </div>
                )}

                {activeTab === 'display' && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                        {kdsCategories.length > 0 ? kdsCategories.map(kds => {
                            const disabled = false;
                            const bgColor = getCategoryBgColor(kds.category);
                            const iconColor = getCategoryIconColor(kds.category);
                            return (
                                <div
                                    key={kds.id}
                                    onClick={() => handleKdsClick(kds)}
                                    style={{
                                        background: C.white,
                                        border: `1.5px solid ${C.border}`,
                                        borderRadius: 14,
                                        padding: '16px 16px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 14,
                                        cursor: disabled ? 'not-allowed' : 'pointer',
                                        opacity: disabled ? 0.5 : 1,
                                        fontFamily: FONT,
                                    }}
                                >
                                    <div style={{
                                        width: 48, height: 48,
                                        background: bgColor,
                                        borderRadius: 12,
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        flexShrink: 0,
                                    }}>
                                        {kds.imageUrl ? renderEntityImage(kds.imageUrl, 26) : getCategoryIcon(kds.iconKey, { size: 22, color: iconColor })}
                                    </div>
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                        <div style={{ fontSize: 15, fontWeight: 700, color: C.text }}>{kds.title}</div>
                                        <div style={{ fontSize: 11, color: C.mutedLight, marginTop: 2 }}>{kds.subtitle}</div>
                                    </div>
                                    <ChevronRight color={C.mutedLight} />
                                </div>
                            );
                        }) : (
                            <div style={{
                                textAlign: 'center', padding: '40px 20px',
                                color: C.mutedLight, fontSize: 13,
                            }}>
                                {t('No display stations configured')}
                            </div>
                        )}
                    </div>
                )}

            </div>

            {/* Employee Auth Modal */}
            <Modal
                isOpen={showEmployeeModal}
                onClose={() => setShowEmployeeModal(false)}
                title={t('Employee Login - {functionTitle}', { functionTitle: selectedFunction?.title })}
            >
                <form onSubmit={handleEmployeeAuth} className="space-y-4">
                    <div style={{ marginBottom: 16 }}>
                        <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>
                            {t('Enter your 6-digit PIN')} <span style={{ color: '#ef4444' }}>*</span>
                        </label>
                        <input
                            type="password"
                            maxLength={6}
                            pattern="[0-9]{6}"
                            inputMode="numeric"
                            placeholder="••••••"
                            value={employeePin}
                            onChange={e => setEmployeePin(e.target.value.replace(/\D/g, '').slice(0, 6))}
                            autoFocus
                            required
                            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                        />
                    </div>
                    <div className="flex justify-end space-x-3 pt-4">
                        <button
                            type="button"
                            onClick={() => setShowEmployeeModal(false)}
                            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                        >
                            {t('Cancel')}
                        </button>
                        <button
                            type="submit"
                            disabled={loading}
                            className="px-4 py-2 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-md shadow-sm hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:bg-gray-400"
                        >
                            {loading ? t('Authenticating...') : t('Login')}
                        </button>
                    </div>
                </form>
            </Modal>
            {renderClockModal()}
        </div>
    );
};

export default FunctionsPage;
